/**
 * SF-157 Component 4: colored type-badge metadata for all 8 `IntelType` values — used by
 * `IntelTargetCard`'s per-type chip row and `DesktopIntelHubShell`'s Type column. Non-component
 * module (mirrors `relationsPresentation.ts`'s precedent for exporting consts from a
 * components-adjacent file, sidestepping `react-refresh/only-export-components`).
 *
 * CB/SOM/SURVEY reuse SF-171's exact tokens (`MobileCrystalBallCard`/`MobileSpyOnMilitaryCard`/
 * `MobileSurveyCard`'s own `CB_FG`/`SM_FG`/`SV_FG`). The other five are new — sourced from
 * `constants/theme.ts`'s existing `COLORS` palette wherever an existing token is a reasonable fit
 * (INFILTRATE: `COLORS.gold`, thievery/gold-theft theme; SOS: `COLORS.link`, this project's own
 * "science pillar" token), otherwise a new literal chosen distinct from every other type and from
 * `COLORS.warning` — verified by eye, not just declared distinct by intent.
 */

import { COLORS } from '../../constants/theme';
import type { IntelType } from '../../types/intel';

export interface IntelTypeMeta {
  label: string;
  full: string;
  fg: string;
  bg: string;
  bd: string;
}

function hexToRgba(hex: string, alpha: number): string {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function meta(label: string, full: string, fg: string): IntelTypeMeta {
  return { label, full, fg, bg: hexToRgba(fg, 0.15), bd: hexToRgba(fg, 0.35) };
}

export const INTEL_TYPE_META: Record<IntelType, IntelTypeMeta> = {
  CB: meta('CB', 'Crystal Ball', '#b89fe0'),
  SOM: meta('SOM', 'Spy on Military', '#8ec4a0'),
  SURVEY: meta('SVY', 'Survey', '#c4b078'),
  INFILTRATE: meta('INF', 'Infiltrate', COLORS.gold),
  SOS: meta('SOS', 'Spy on Science', COLORS.link),
  SOHD: meta('SOHD', 'Spy on Home Defense', '#d4614f'),
  SOT: meta('SOT', 'Spy on Throne', '#5a92c9'),
  CE: meta('CE', 'Crystal Eye', '#c46a9e'),
};
